import { apim } from "@/constants/api"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { HYDRATE } from "next-redux-wrapper"
import _get from "lodash/get"
import { getQuantityCount } from "@/utils/cart"
import { store } from "@/store"
import { setAnonymousId } from "@/store/features/authSlice"
import { showToast } from "@/store/features/genericSlice"
import { getProductsBySlugLW, getProductsBySkuLW } from "@/services/lw.service"
import { getConfig } from "@/constants/config"
const initialState = {
  cart: {
    id: "",
  },
  activeCart: { cartId: "", version: "" },
  quantity: 0,
  cartCount: 0,
  shareCartCount: 0,
  cartUrl: "",
  status: "",
  lwCartPdts: [],
  loading: false,
  sharedCarts: {},
  isSharedCartLoading: false,
  lwSharedCarts: [],
  activeCarts: [],
  addLineItems: {},
  isActiveCart: false,
  isActiveCartLoading: false,
  isSharedCartModal: false,
}
export const getActiveCart = createAsyncThunk(
  "getActiveCart",
  async (cartType = "", thunkAPI) => {
    try {
      const response = await apim.get("/cart/active?expand=calculation")
      const { data, status } = response
      if (status !== 200) {
        thunkAPI.dispatch(
          showToast({
            message: "Failed to Fetch Cart",
            isVisible: true,
          })
        )
      }
      const cartRes =
        status === 200
          ? data.filter(cart => cart.cartType === cartType)?.[0] ?? {}
          : { error: "cart failed" }
      return cartRes
    } catch {
      return { error: "cart failed" }
    }
  }
)
export const getCart = createAsyncThunk(
  "getCart",
  async (cartType = "", thunkAPI) => {
    let cartId = ""
    let cartError = ""
    const res = await thunkAPI
      .dispatch(getActiveCart(cartType))
      .unwrap()
      .then(res => res)
    cartId = res.cartId ? res.cartId : ""
    cartError = res.error ?? ""
    if (cartId) {
      const response = await apim.get(`/cart/${cartId}?expand=calculation`)
      if (response?.data?.anonymousId) {
        const { anonymousId } = response.data
        store.dispatch(setAnonymousId(anonymousId))
      } else {
        store.dispatch(setAnonymousId(""))
      }
      if (response.status !== 200) {
        thunkAPI.dispatch(
          showToast({
            message: "Failed to Fetch Cart",
            isVisible: true,
          })
        )
      }
      return response.status === 200
        ? response?.data
        : { lineItems: [], error: "failed to fetch cart" }
    } else if (!cartError) {
      return { cartExists: "false", error: "No active cart exists." }
    } else {
      return { lineItems: [], error: "failed to fetch cart" }
    }
  }
)

export const createCart = createAsyncThunk(
  "createCart",
  async (body, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      auth: { user },
    } = state
    const payload = { ...body }
    if (user) payload.customerEmail = user.sub?.toLowerCase() ?? ""
    const response = await apim.post(`/cart/create`, payload)
    if (response?.data?.anonymousId) {
      const { anonymousId } = response.data
      store.dispatch(setAnonymousId(anonymousId))
    } else {
      store.dispatch(setAnonymousId(""))
    }
    if (!response?.data?.id) {
      thunkAPI.dispatch(
        showToast({
          message: "Failed to Create Cart",
          isVisible: true,
        })
      )
    }
    return response.data
  }
)

export const refreshCart = createAsyncThunk(
  "refreshCart",
  async (body, thunkAPI) => {
    const cartRes =
      (await thunkAPI
        .dispatch(getActiveCart())
        .unwrap()
        .then(res => res)
        .catch(() => thunkAPI.dispatch(setCartLoading(false)))) || {}
    const { cartId = "", version = 0, error = "" } = cartRes
    if (cartId) {
      const payload = {
        version,
        actions: [
          {
            action: "recalculate",
            updateProductData: true,
          },
        ],
      }
      try {
        const res = await apim.post(
          `/cart/items?cartId=${cartId}&expand=calculation`,
          payload
        )
        const { status, data = {} } = res
        if (data.anonymousId) {
          store.dispatch(setAnonymousId(data.anonymousId))
        } else {
          store.dispatch(setAnonymousId(""))
        }
        if (status === 200) {
          return data
        }
      } catch (err) {
        thunkAPI.dispatch(setCartLoading(false))
        return { lineItems: [], error: "failed to refresh cart" }
      }
    } else if (!error) {
      thunkAPI.dispatch(setCartLoading(false))
      return { cartExists: "false", error: "No active cart exists." }
    } else {
      thunkAPI.dispatch(setCartLoading(false))
      return { lineItems: [], error: "failed to fetch cart" }
    }
  }
)

export const shareCart = createAsyncThunk("sharecart", async body => {
  const response = await apim.post("/cart/sharedcart", body)
  return response.data
})

export const getSharedCarts = createAsyncThunk("sharedcart", async sid => {
  return apim.get(`cart/sharedcart/${sid}`).then(async res => {
    const { data = {} } = res
    store
      .dispatch(
        getProductByLw({
          resData: { ...data, lineItems: data?.lineitems },
          isSharedCart: true,
        })
      )
      .unwrap()
      .then(slugData => {
        const cartItems = []
        let specFiles = []
        data?.lineitems.map((item, i) => {
          const sku = item?.sku
          const lwProduct =
            slugData?.filter(pdt => pdt["sku_s"] === sku)?.[0] ?? {}
          let image = ""
          const productVariantImages = _get(
            lwProduct,
            "productImages.labelWithUrl_ss",
            []
          )
          const variantImages =
            productVariantImages.filter(
              img => sku === img.split("|")[0]
            )?.[0] ??
            productVariantImages[0] ??
            ""
          image = variantImages?.split("|")?.[1] ?? ""
          let colors = []
          if (lwProduct["Color.SKU.Details_ss"]) {
            colors = lwProduct["Color.SKU.Details_ss"].filter(color => {
              const d = color.split("|")
              return d[2] === sku
            })
          }
          const specFile =
            lwProduct[
              "ProductResource.SpecPDFFileName.resourceFullWebURL_ss"
            ] ?? []
          specFiles = [...specFiles, ...specFile]
          cartItems[i] = {
            id: _get(item, "id", ""),
            sku: sku,
            slug: lwProduct.slug_s,
            quantity: _get(item, "qty", 0),
            image: image,
            color: colors?.[0]?.split("|")?.[0],
            category: lwProduct["ProductLocalCategory_s"],
            name: lwProduct["Product_Title_s"],
            desc: lwProduct["Product_Description_s"],
            unitPrice: Number(lwProduct["priceList.GST.price_d"] ?? 0).toFixed(
              2
            ),
            totalPrice: Number(
              lwProduct["priceList.GST.finalPrice_d"] * item?.qty
            ).toFixed(2),
            addedAt: _get(item, "addedAt", ""),
          }
        })
        const { lineitems, ...filterData } = data
        if (lineitems) {
          filterData.lineItems = cartItems
        }
        filterData.specFiles = specFiles
        const items = cartItems?.map(({ sku, quantity }) => ({
          sku,
          quantity,
          action: "addLineItem",
        }))
        const shareCartCount = cartItems.reduce(
          (acc, { quantity }) => quantity + acc,
          0
        )
        store.dispatch(setSharedCartCount(shareCartCount))
        store.dispatch(setAddLineItems({ actions: items }))
        store.dispatch(setSharedCarts(filterData))
        store.dispatch(setIsSharedCartLoading(false))
      })
    return data
  })
})

export const mergeCart = createAsyncThunk(
  "cart/merge",
  async (body, thunkAPI) => {
    const {
      cart: {
        cart: { anonymousId = "" },
      },
    } = thunkAPI.getState()
    const payload = {
      anonymousId: anonymousId,
    }
    const {
      data: { FGCart = {} },
    } = await apim.post("/cart/merge?expand=calculation", payload)
    if (FGCart.id) {
      store.dispatch(setAnonymousId(""))
    }
    return FGCart
  }
)

export const addToCart = createAsyncThunk(
  "addToCart",
  async (body, thunkAPI) => {
    const res = await thunkAPI
      .dispatch(getActiveCart())
      .unwrap()
      .then(res => res)
    const { cartId = "", version } = res
    let id = cartId
    let cartVer = version
    if (!cartId && !version) {
      const CONFIG = await getConfig()
      const currencyCode = CONFIG?.internationalization?.currencyCode ?? ""
      const newCart = await thunkAPI
        .dispatch(createCart({ currency: currencyCode, taxMode: "Platform" }))
        .unwrap()
        .then(res => res)
      id = newCart.id
      cartVer = newCart.version
    }

    const payload = { version: cartVer, ...body }
    const response = await apim
      .post(`/cart/items?cartId=${id}&expand=calculation`, payload)
      .then(addRes => {
        thunkAPI.dispatch(getProductByLw({ resData: addRes.data }))
        return addRes
      })
      .catch(() => {
        thunkAPI.dispatch(setCartLoading(false))
        thunkAPI.dispatch(
          showToast({
            message: "Failed to Add to cart",
            isVisible: true,
          })
        )
      })
    if (response?.data?.anonymousId) {
      const { anonymousId } = response.data
      store.dispatch(setAnonymousId(anonymousId))
      store.dispatch(setIsActiveCart(false))
    } else {
      store.dispatch(setAnonymousId(""))
    }
    return response.data
  }
)
export const removeFromCartLineItem = createAsyncThunk(
  "removeFromCartLineItem",
  async (body, thunkAPI) => {
    const res = await thunkAPI
      .dispatch(getActiveCart())
      .unwrap()
      .then(res => res)
    const { cartId = "", version } = res
    body.version = version
    const response = await apim
      .post(`/cart/items?cartId=${cartId}&expand=calculation`, body)
      .then(addRes => addRes)
      .catch(() => {
        thunkAPI.dispatch(setCartLoading(false))
        thunkAPI.dispatch(
          showToast({
            message: "Failed to Remove from Cart",
            isVisible: true,
          })
        )
      })
    return response.data
  }
)
export const deleteCart = createAsyncThunk(
  "deleteCart",
  async (body, thunkAPI) => {
    const res = await thunkAPI
      .dispatch(getActiveCart())
      .unwrap()
      .then(res => res)
    const { cartId = "", version } = res
    const response = await apim.delete(
      `/cart/${cartId}?cart-versionId=${version}`,
      body
    )
    return response.data
  }
)

export const discount = createAsyncThunk("discount", async (body, thunkAPI) => {
  const res = await thunkAPI
    .dispatch(getActiveCart())
    .unwrap()
    .then(res => res)
  const { cartId = "", version } = res
  body.version = version
  const response = await apim.post(
    `/cart/discount?cartId=${cartId}&expand=calculation`,
    body
  )
  return response.data
})

export const getProductByLw = createAsyncThunk(
  "getProductBySlug",
  async (data, thunkAPI) => {
    const { resData, isSharedCart = false } = data
    if (resData.lineItems?.length) {
      const currentCart = thunkAPI.getState()?.cart
      const CONFIG = await getConfig()
      const locale = CONFIG?.internationalization?.locale ?? ""
      const fl = [
        "productImages.labelWithUrl_ss",
        "Product_Title_s",
        "Product_Description_s",
        "masterSKU_s",
        "ProductLocalCategory_s",
        "slug_s",
        "Color.SKU.Details_ss",
        "priceList.GST.price_d",
        "priceList.GST.finalPrice_d",
        "ProductResource.SpecPDFFileName.resourceFullWebURL_ss",
        "cashbackPromo_s",
        "sku_s"
      ]
      const productsSlug = resData.lineItems?.map(
        product => product?.productSlug?.[locale] ?? product?.sku
      )
      const currentLwPdts =
        (isSharedCart ? currentCart.lwSharedCarts : currentCart.lwCartPdts) ??
        []
      const slugProducts = productsSlug.filter(pdt => {
        if (!currentLwPdts.map(cartPdt => cartPdt["slug_s"]).includes(pdt)) {
          return pdt
        }
      })
      if (slugProducts?.length) {
        console.log("slugProducts", slugProducts)
        console.log("isSharedCart", isSharedCart)
        console.log("currentLwPdts", currentLwPdts)
        const lwData = isSharedCart
          ? await getProductsBySkuLW(slugProducts, fl)
          : (await getProductsBySlugLW(slugProducts, fl)) ?? []
        if (!isSharedCart) {
          thunkAPI.dispatch(
            setLwCartPdts(
              currentLwPdts.length ? [...currentLwPdts, ...lwData] : lwData
            )
          )
        }
        return currentLwPdts.length ? [...currentLwPdts, ...lwData] : lwData
      }
    } else {
      thunkAPI.dispatch(setLwCartPdts([]))
      thunkAPI.dispatch(setSharedCarts([]))
    }
  }
)

export const shippingUpdate = createAsyncThunk(
  "shippingUpdate",
  async (body, thunkAPI) => {
    const { cartId = "", version } = await thunkAPI
      .dispatch(getActiveCart())
      .unwrap()
      .then(res => res)
    body.version = version
    const response = await apim
      .post(`/cart/items?cartId=${cartId}&expand=calculation`, body)
      .then(addRes => addRes)
      .catch(() => {
        thunkAPI.dispatch(setCartLoading(false))
        thunkAPI.dispatch(
          showToast({
            message: "Failed to set Shipping address",
            isVisible: true,
          })
        )
      })
    return response.data
  }
)

export const submitOrder = createAsyncThunk(
  "/order/submit",
  async (body, thunkAPI) => {
    const res = await thunkAPI
      .dispatch(getActiveCart())
      .unwrap()
      .then(res => res)
    const { cartId = "", version } = res
    body.cartId = cartId
    body.version = version
    const response = await apim.post("/checkout/order/submit", body)
    return response.data
  }
)

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCart(state, action) {
      state.cart = action.payload
    },
    setActiveCart(state, action) {
      state.activeCart = action.payload
    },
    setRefreshCart(state, action) {
      state.cart = action.payload
    },
    setCartCount(state, action) {
      state.cartCount = action.payload
    },
    setSharedCartCount(state, action) {
      state.shareCartCount = action.payload
    },
    setCartUrl(state, action) {
      state.cartUrl = action.payload
    },
    increment(state, action) {
      state.quantity = state.quantity + 1
    },
    decrement(state, action) {
      state.quantity = state.quantity - 1
    },
    setLwCartPdts(state, action) {
      state.lwCartPdts = action.payload
    },
    setCartLoading(state, action) {
      state.loading = action.payload
    },
    setSharedCarts(state, action) {
      state.sharedCarts = action.payload
    },
    setIsSharedCartLoading(state, action) {
      state.isSharedCartLoading = action.payload
    },
    setIsSharedCartModal(state, action) {
      state.isSharedCartModal = action.payload
    },
    setAddLineItems(state, action) {
      state.addLineItems = action.payload
    },
    setIsActiveCart(state, action) {
      state.isActiveCart = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase([HYDRATE], (state, action) => {
        return {
          ...state,
          ...action.payload.cart,
        }
      })
      .addCase(getActiveCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getActiveCart.fulfilled, (state, action) => {
        state.activeCart = action.payload
        state.status = "succeeded"
      })
      .addCase(getActiveCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getCart.pending, (state, action) => {
        state.status = "loading"
        state.isActiveCartLoading = true
      })
      .addCase(getCart.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
        state.activeCarts = action.payload
        state.isActiveCartLoading = false
      })
      .addCase(getCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
        state.isActiveCartLoading = false
      })
      .addCase(createCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(createCart.fulfilled, (state, action) => {
        state.cart = action.payload
        state.status = "succeeded"
      })
      .addCase(createCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(refreshCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(refreshCart.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
        state.activeCarts = action.payload
        state.isActiveCartLoading = false
      })
      .addCase(refreshCart.rejected, (state, action) => {
        state.status = "failed"
      })
      .addCase(shareCart.pending, state => {
        state.status = "loading"
      })
      .addCase(shareCart.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(shareCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getSharedCarts.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getSharedCarts.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(getSharedCarts.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addToCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(deleteCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(deleteCart.fulfilled, (state, action) => {
        state.cart = { id: action.payload.id, lineItems: [] }
        state.cartCount = 0
        state.status = "succeeded"
      })
      .addCase(deleteCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(discount.pending, state => {
        state.status = "loading"
      })
      .addCase(discount.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.cart = action.payload
      })
      .addCase(discount.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(mergeCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(mergeCart.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
      })
      .addCase(mergeCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(removeFromCartLineItem.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(removeFromCartLineItem.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
      })
      .addCase(removeFromCartLineItem.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(getProductByLw.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getProductByLw.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(getProductByLw.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shippingUpdate.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(shippingUpdate.fulfilled, (state, action) => {
        state.cart = action.payload
        state.cartCount = getQuantityCount(action.payload?.lineItems) ?? 0
        state.status = "succeeded"
      })
      .addCase(shippingUpdate.rejected, (state, action) => {
        state.status = "failed"
      })
      .addCase(submitOrder.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(submitOrder.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(submitOrder.rejected, (state, action) => {
        state.status = "failed"
      })
  },
})

export const {
  setCart,
  setActiveCart,
  increment,
  decrement,
  setCartCount,
  setSharedCartCount,
  setCartUrl,
  setLwCartPdts,
  setSharedCarts,
  setCartLoading,
  setIsSharedCartLoading,
  setIsSharedCartModal,
  setAddLineItems,
  setIsActiveCart,
} = cartSlice.actions
export const selectCartState = state => state.cart
export default cartSlice.reducer
